import React, { useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Collapse, Form, ProgressBar, Row, Spinner, Table } from 'react-bootstrap';
import { GitAdminIssue, GitIterationWorkload } from '../../interfaces';

type Props = {
  issues: GitAdminIssue[];
  fetchWorkloadByUser: (iteration: string, includeClosedIssues: boolean) => Promise<GitIterationWorkload>;
  triggerRefreshAll: (request: { gitLabApiBaseUrl: string; gitToken: string }) => Promise<{ operationId: string; status: string }>;
  getRefreshStatus: (operationId: string) => Promise<{ status: string; error?: string; totalActions?: number; completedActions?: number; currentAction?: string }>;
  formatDuration: (seconds: number) => string;
  gitLabApiBaseUrl: string;
  gitToken: string;
};

type GitLabMilestone = {
  title?: string;
  start_date?: string;
  due_date?: string;
  state?: string;
};

type WorkdayProgress = {
  elapsed: number;
  total: number;
  ratio: number;
};

const toUtcDay = (value: Date): Date => new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));

const parseDateOnlyUtc = (value?: string): Date | null => {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) ? date : null;
};

const addUtcDays = (value: Date, days: number): Date => {
  const date = new Date(value.getTime());
  date.setUTCDate(date.getUTCDate() + days);
  return date;
};

const isWorkingDay = (value: Date): boolean => {
  const day = value.getUTCDay();
  return day >= 1 && day <= 5;
};

const countWorkingDaysInclusive = (start: Date, end: Date): number => {
  if (start.getTime() > end.getTime()) {
    return 0;
  }

  let count = 0;
  let current = toUtcDay(start);
  const max = toUtcDay(end);

  while (current.getTime() <= max.getTime()) {
    if (isWorkingDay(current)) {
      count += 1;
    }

    current = addUtcDays(current, 1);
  }

  return count;
};

const formatSnapshotAt = (value?: string): string => {
  if (!value) {
    return 'N/A';
  }

  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed)) {
    return value;
  }

  return new Date(parsed).toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'medium'
  });
};

const GitAdminUserWorkloadCard: React.FC<Props> = ({
  issues,
  fetchWorkloadByUser,
  triggerRefreshAll,
  getRefreshStatus,
  formatDuration,
  gitLabApiBaseUrl,
  gitToken
}) => {
  const [showCard, setShowCard] = useState(false);
  const [iteration, setIteration] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncingGitLab, setSyncingGitLab] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [refreshActionLabel, setRefreshActionLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [payload, setPayload] = useState<GitIterationWorkload | null>(null);
  const [currentIterationLoading, setCurrentIterationLoading] = useState(false);
  const [milestonesByTitle, setMilestonesByTitle] = useState<Record<string, GitLabMilestone>>({});

  const iterationOptions = useMemo(() => {
    const unique = new Set<string>();

    issues.forEach(issue => {
      const value = (issue.iterationName ?? '').trim();
      if (value) {
        unique.add(value);
      }
    });

    return Array.from(unique).sort((left, right) => left.localeCompare(right));
  }, [issues]);

  const chartUsers = useMemo(() => {
    if (!payload) {
      return [];
    }

    return [...payload.users]
      .sort((left, right) => right.totalTimeEstimateSeconds - left.totalTimeEstimateSeconds)
      .map(user => ({
        ...user,
        label: user.name ?? user.userName ?? 'Non assigne'
      }));
  }, [payload]);

  const chartMax = useMemo(() => {
    if (!payload || chartUsers.length === 0) {
      return 1;
    }

    const maxEstimate = Math.max(...chartUsers.map(user => Math.max(0, user.totalTimeEstimateSeconds)));
    const maxSpent = Math.max(...chartUsers.map(user => Math.max(0, user.totalTimeSpentSeconds)));
    return Math.max(1, maxEstimate, maxSpent);
  }, [payload, chartUsers]);

  const activeMilestone = useMemo(() => {
    const key = iteration.trim().toLowerCase();
    if (!key) {
      return null;
    }

    return milestonesByTitle[key] ?? null;
  }, [iteration, milestonesByTitle]);

  const workdayProgress = useMemo((): WorkdayProgress | null => {
    if (!activeMilestone) {
      return null;
    }

    const start = parseDateOnlyUtc(activeMilestone.start_date);
    const end = parseDateOnlyUtc(activeMilestone.due_date);
    if (!start || !end) {
      return null;
    }

    const total = countWorkingDaysInclusive(start, end);
    if (total <= 0) {
      return null;
    }

    const today = toUtcDay(new Date());
    let elapsedEnd = addUtcDays(today, -1);
    if (today.getTime() > end.getTime()) {
      elapsedEnd = end;
    }

    const elapsed = elapsedEnd.getTime() < start.getTime()
      ? 0
      : countWorkingDaysInclusive(start, elapsedEnd);

    return {
      elapsed,
      total,
      ratio: Math.max(0, Math.min(1, elapsed / total))
    };
  }, [activeMilestone]);

  const pickCurrentMilestone = (milestones: GitLabMilestone[]): string | null => {
    if (!Array.isArray(milestones) || milestones.length === 0) {
      return null;
    }

    const withCurrentState = milestones.find(item => (item.state ?? '').toLowerCase() === 'active' && (item.title ?? '').trim());
    if (withCurrentState?.title?.trim()) {
      return withCurrentState.title.trim();
    }

    const today = new Date();
    const currentByDate = milestones.find(item => {
      const start = item.start_date ? Date.parse(item.start_date) : Number.NaN;
      const end = item.due_date ? Date.parse(item.due_date) : Number.NaN;
      if (!Number.isFinite(start) || !Number.isFinite(end)) {
        return false;
      }

      const date = today.getTime();
      return start <= date && date <= end;
    });

    if (currentByDate?.title?.trim()) {
      return currentByDate.title.trim();
    }

    return (milestones[0].title ?? '').trim() || null;
  };

  const fetchHobudMilestones = async (): Promise<GitLabMilestone[]> => {
    if (!gitLabApiBaseUrl?.trim() || !gitToken?.trim()) {
      return [];
    }

    const base = gitLabApiBaseUrl.replace(/\/$/, '');
    const encodedGroupPath = encodeURIComponent('hobud');
    const headers = {
      'Authorization': `Bearer ${gitToken}`,
      'Content-Type': 'application/json'
    };

    try {
      const response = await fetch(`${base}/groups/${encodedGroupPath}/milestones?state=all&per_page=100`, { method: 'GET', headers });
      if (!response.ok) {
        return [];
      }

      return await response.json() as GitLabMilestone[];
    } catch {
      return [];
    }
  };

  const updateMilestonesCache = (milestones: GitLabMilestone[]) => {
    const nextState: Record<string, GitLabMilestone> = {};
    milestones.forEach(milestone => {
      const key = (milestone.title ?? '').trim().toLowerCase();
      if (key) {
        nextState[key] = milestone;
      }
    });

    setMilestonesByTitle(nextState);
  };

  const fetchCurrentHobudMilestone = async (): Promise<string | null> => {
    const milestones = await fetchHobudMilestones();
    if (milestones.length > 0) {
      updateMilestonesCache(milestones);
    }

    const found = pickCurrentMilestone(milestones);
    if (found) {
      return found;
    }

    return null;
  };

  const loadWorkload = async () => {
    const normalizedIteration = iteration.trim();
    if (!normalizedIteration) {
      setError('Veuillez sélectionner ou saisir une itération.');
      return;
    }

    setLoading(true);
    setError(null);
    let refreshCompleted = false;

    try {
      setSyncingGitLab(true);
      setRefreshProgress(8);
      setRefreshActionLabel('Queued');

      if (!gitLabApiBaseUrl?.trim() || !gitToken?.trim()) {
        throw new Error('Configuration GitLab manquante pour actualiser les issues.');
      }

      const refreshResult = await triggerRefreshAll({
        gitLabApiBaseUrl,
        gitToken
      });

      const operationId = refreshResult?.operationId;
      if (!operationId) {
        throw new Error('Impossible de lancer le refresh GitLab.');
      }

      let completed = false;
      for (let attempt = 0; attempt < 120; attempt += 1) {
        const statusPayload = await getRefreshStatus(operationId);
        const status = (statusPayload?.status ?? '').toLowerCase();
        const totalActions = Math.max(0, statusPayload?.totalActions ?? 0);
        const completedActions = Math.max(0, statusPayload?.completedActions ?? 0);

        if (statusPayload?.currentAction) {
          setRefreshActionLabel(statusPayload.currentAction);
        }

        if (totalActions > 0) {
          const progress = Math.max(1, Math.min(95, Math.round((completedActions / totalActions) * 100)));
          setRefreshProgress(progress);
        } else if (status === 'running') {
          setRefreshProgress(prev => Math.max(prev, 25));
        }

        if (status === 'completed') {
          completed = true;
          refreshCompleted = true;
          setRefreshProgress(100);
          setRefreshActionLabel('Completed');
          break;
        }

        if (status === 'failed') {
          setRefreshActionLabel('Failed');
          throw new Error(statusPayload?.error || 'Le refresh GitLab a échoué.');
        }

        await new Promise(resolve => setTimeout(resolve, 1500));
      }

      if (!completed) {
        setRefreshActionLabel('Timeout');
        throw new Error('Le refresh GitLab est trop long, réessaie dans quelques instants.');
      }

      if (!activeMilestone) {
        const milestones = await fetchHobudMilestones();
        if (milestones.length > 0) {
          updateMilestonesCache(milestones);
        }
      }

      setSyncingGitLab(false);
      setTimeout(() => {
        setRefreshProgress(0);
        setRefreshActionLabel(null);
      }, 500);

      const result = await fetchWorkloadByUser(normalizedIteration, true);
      setPayload(result);
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Impossible de charger le workload.';
      setError(message);
      setPayload(null);
    } finally {
      setSyncingGitLab(false);
      setLoading(false);
      if (!refreshCompleted) {
        setRefreshProgress(0);
        setRefreshActionLabel(null);
      }
    }
  };

  const loadCurrentIterationWorkload = async () => {
    setCurrentIterationLoading(true);
    setError(null);

    try {
      const current = await fetchCurrentHobudMilestone();
      if (!current) {
        setError('Impossible de détecter la milestone courante du groupe hobud.');
        return;
      }

      setIteration(current);
      setLoading(true);
      const result = await fetchWorkloadByUser(current, true);
      setPayload(result);
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Impossible de charger le workload de la milestone courante.';
      setError(message);
      setPayload(null);
    } finally {
      setLoading(false);
      setCurrentIterationLoading(false);
    }
  };

  return (
    <Card className="shadow-sm mb-4">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <span>Workload par utilisateur</span>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={() => {
            setShowCard(value => {
              const next = !value;
              if (next && !payload && !loading && !currentIterationLoading) {
                loadCurrentIterationWorkload().catch(() => undefined);
              }
              return next;
            });
          }}
        >
          {showCard ? 'Masquer' : 'Afficher'}
        </Button>
      </Card.Header>
      <Collapse in={showCard}>
        <div>
          <Card.Body>
            <Row className="g-3 mb-3">
              <Col lg={6}>
                <Form.Group>
                  <Form.Label>Itération</Form.Label>
                  <Form.Control
                    type="text"
                    list="gitadmin-iteration-options"
                    placeholder="Ex: Sprint 42"
                    value={iteration}
                    onChange={(event) => setIteration(event.target.value)}
                  />
                  <datalist id="gitadmin-iteration-options">
                    {iterationOptions.map(option => (
                      <option key={option} value={option} />
                    ))}
                  </datalist>
                  {iterationOptions.length === 0 && (
                    <Form.Text className="text-muted">Aucune itération détectée localement, vous pouvez d'abord lancer un refresh.</Form.Text>
                  )}
                </Form.Group>
              </Col>
              <Col lg={6} className="d-flex align-items-end justify-content-lg-end">
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    disabled={loading || currentIterationLoading || syncingGitLab}
                    onClick={loadCurrentIterationWorkload}
                  >
                    {currentIterationLoading ? 'Detection...' : 'Milestone courante'}
                  </Button>
                  <Button
                    variant="outline-primary"
                    disabled={loading || currentIterationLoading || syncingGitLab}
                    onClick={loadWorkload}
                  >
                    {syncingGitLab ? 'Actualisation GitLab...' : loading ? 'Chargement...' : 'Charger workload'}
                  </Button>
                </div>
              </Col>
            </Row>

            {error && (
              <Alert variant="warning" className="mb-3">
                {error}
              </Alert>
            )}

            {loading && (
              <div className="d-flex align-items-center gap-2 text-muted mb-3">
                <Spinner animation="border" size="sm" />
                {syncingGitLab ? 'Actualisation des issues/tasks depuis GitLab...' : 'Chargement du workload...'}
              </div>
            )}

            {syncingGitLab && (
              <div className="mb-3">
                <div className="small text-muted mb-1">
                  {refreshActionLabel ? `Refresh GitLab: ${refreshActionLabel}` : 'Refresh GitLab en cours...'}
                </div>
                <ProgressBar now={refreshProgress} animated={refreshProgress > 0 && refreshProgress < 100} label={`${refreshProgress}%`} />
              </div>
            )}

            {payload && !loading && (
              <>
                <div className="d-flex flex-wrap gap-3 mb-3">
                  <Badge bg="secondary">Itération: {payload.iteration}</Badge>
                  <Badge bg="dark">Snapshot: {formatSnapshotAt(payload.snapshotAt)}</Badge>
                  <Badge bg="primary">Issues: {payload.totalIssueCount}</Badge>
                  <Badge bg="info">Estimé: {formatDuration(payload.totalTimeEstimateSeconds)}</Badge>
                  <Badge bg="success">Consommé: {formatDuration(payload.totalTimeSpentSeconds)}</Badge>
                  {workdayProgress && (
                    <Badge bg="warning" text="dark">
                      Reference: {workdayProgress.elapsed}/{workdayProgress.total} j ouvres ({Math.round(workdayProgress.ratio * 100)}%) - {workdayProgress.elapsed * 8}h/{workdayProgress.total * 8}h (8h/j)
                    </Badge>
                  )}
                  <Badge bg={payload.remainingTimeSeconds > 0 ? 'warning' : 'secondary'}>
                    Restant: {formatDuration(payload.remainingTimeSeconds)}
                  </Badge>
                </div>

                {chartUsers.length === 0 ? (
                  <div className="text-muted">Aucune donnée utilisateur pour cette itération.</div>
                ) : (
                  <div className="gitadmin-workload-chart">
                    <div className="gitadmin-workload-legend">
                      <span className="gitadmin-workload-legend-item">
                        <span className="gitadmin-workload-swatch gitadmin-workload-swatch-estimate" />
                        Estime
                      </span>
                      <span className="gitadmin-workload-legend-item">
                        <span className="gitadmin-workload-swatch gitadmin-workload-swatch-spent" />
                        Consomme
                      </span>
                    </div>

                    <div className="gitadmin-workload-grid">
                      {chartUsers.map(user => {
                        const estimateWidth = (Math.max(0, user.totalTimeEstimateSeconds) / chartMax) * 100;
                        const spentWidth = (Math.max(0, user.totalTimeSpentSeconds) / chartMax) * 100;
                        const expectedSeconds = workdayProgress
                          ? Math.round(Math.max(0, user.totalTimeEstimateSeconds) * workdayProgress.ratio)
                          : 0;
                        const expectedWidth = (Math.max(0, expectedSeconds) / chartMax) * 100;
                        const key = `${user.gitUserId ?? 'none'}-${user.gitlabUserId ?? 'none'}-${user.userName ?? 'unknown'}`;

                        return (
                          <div className="gitadmin-workload-row" key={key}>
                            <div className="gitadmin-workload-user">
                              <div className="gitadmin-workload-user-name">{user.label}</div>
                              <div className="gitadmin-workload-user-meta">
                                {user.issueCount} issue(s)
                                {user.userName ? ` · ${user.userName}` : ''}
                              </div>
                            </div>

                            <div className="gitadmin-workload-bars">
                              <div className="gitadmin-workload-track">
                                <div
                                  className="gitadmin-workload-bar gitadmin-workload-bar-estimate"
                                  style={{ width: `${Math.max(1, estimateWidth)}%` }}
                                  title={`Estime: ${formatDuration(user.totalTimeEstimateSeconds)}`}
                                />
                              </div>
                              <div className="gitadmin-workload-track">
                                <div
                                  className="gitadmin-workload-bar gitadmin-workload-bar-spent"
                                  style={{ width: `${Math.max(1, spentWidth)}%` }}
                                  title={`Consomme: ${formatDuration(user.totalTimeSpentSeconds)}`}
                                />
                                {workdayProgress && (
                                  <span
                                    className="gitadmin-workload-reference-marker"
                                    style={{ left: `${Math.max(0, Math.min(100, expectedWidth))}%` }}
                                    title={`Attendu a date (8h/j, lun-ven): ${formatDuration(expectedSeconds)}`}
                                  />
                                )}
                              </div>
                            </div>

                            <div className="gitadmin-workload-values">
                              <div>E: {formatDuration(user.totalTimeEstimateSeconds)}</div>
                              <div>C: {formatDuration(user.totalTimeSpentSeconds)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="mt-4">
                  <h6 className="mb-3">Issues et tasks sans assignee</h6>
                  {payload.unassignedIssues.length === 0 ? (
                    <div className="text-muted">Aucune issue ou task non assignée sur cette itération.</div>
                  ) : (
                    <div className="table-responsive">
                      <Table striped hover size="sm" className="align-middle mb-0">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>#</th>
                            <th>Titre</th>
                            <th>Etat</th>
                            <th>Estime</th>
                            <th>Consomme</th>
                            <th>Lien</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payload.unassignedIssues.map(item => (
                            <tr key={item.id}>
                              <td>{item.isTask ? 'Task' : 'Issue'}</td>
                              <td>#{item.gitlabIssueIid}</td>
                              <td>{item.title}</td>
                              <td>{item.state ?? '-'}</td>
                              <td>{formatDuration(item.timeEstimateSeconds)}</td>
                              <td>{formatDuration(item.timeSpentSeconds)}</td>
                              <td>
                                {item.webUrl ? (
                                  <a href={item.webUrl} target="_blank" rel="noopener noreferrer">Ouvrir</a>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </div>
              </>
            )}
          </Card.Body>
        </div>
      </Collapse>
    </Card>
  );
};

export default GitAdminUserWorkloadCard;
